import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUserId, planetGet } from "../../api/planetApi";

export default function PlanetHomePage() {
  const userId = getUserId();
  const [dash, setDash] = useState(null);

  useEffect(() => {
    if (!userId) {
      setDash(null);
      return;
    }
    planetGet(`/dashboard?user_id=${userId}`)
      .then(setDash)
      .catch(() => setDash(null));
  }, [userId]);

  return (
    <div className="space-y-10">
      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-900 via-teal-800 to-slate-900 text-teal-50 px-6 py-12 sm:px-10">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(94,234,212,0.35), transparent 40%), radial-gradient(circle at 80% 70%, rgba(251,113,133,0.25), transparent 35%)",
          }}
        />
        <div className="relative max-w-lg">
          <h2 className="text-2xl sm:text-3xl font-semibold leading-snug">
            在模糊的坐标里，留下一封可被接住的信
          </h2>
          <p className="mt-3 text-sm text-teal-100/90 leading-relaxed">
            先公开城市级位置，再投漂流瓶。对方领取、阅读、回信后，可发起认识邀请并进入站内私信。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/planet/singles"
              className="px-4 py-2 rounded-lg bg-teal-300 text-teal-950 text-sm font-medium hover:bg-teal-200"
            >
              打开单身地图
            </Link>
            <Link
              to="/planet/bottles/create"
              className="px-4 py-2 rounded-lg border border-teal-400/50 text-sm hover:bg-white/10"
            >
              投一封漂流瓶
            </Link>
          </div>
          {!userId && (
            <p className="mt-4 text-xs text-teal-200/80">
              投递与领取需先{" "}
              <Link to="/login" className="underline">
                登录
              </Link>
            </p>
          )}
        </div>
      </section>

      {userId && dash && (
        <section className="bg-white border border-teal-100 rounded-xl p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-medium text-teal-950">我的星球进度</h3>
            <p className="text-xs text-slate-400">
              {dash.auto_approve
                ? "当前投瓶可自动过审匹配（演示模式）"
                : "投瓶需后台人工审核"}
            </p>
          </div>
          <ol className="space-y-2">
            {(dash.steps || []).map((s, idx) => (
              <li key={s.key}>
                <Link
                  to={s.to}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm border ${
                    s.done
                      ? "border-teal-200 bg-teal-50/60 text-teal-900"
                      : "border-amber-100 bg-amber-50/50 text-amber-900"
                  }`}
                >
                  <span className="w-6 h-6 rounded-full bg-white border flex items-center justify-center text-xs font-semibold">
                    {s.done ? "✓" : idx + 1}
                  </span>
                  <span className="flex-1">{s.label}</span>
                  <span className="text-xs opacity-70">{s.done ? "已完成" : "去设置"}</span>
                </Link>
              </li>
            ))}
          </ol>

          {(dash.inbox_pending > 0 ||
            dash.invites_pending > 0 ||
            dash.sent_pending_audit > 0) && (
            <div className="flex flex-wrap gap-2 text-sm pt-1">
              {dash.inbox_pending > 0 && (
                <Link
                  to="/planet/bottles?role=inbox"
                  className="px-3 py-1.5 rounded-lg bg-rose-600 text-white"
                >
                  有 {dash.inbox_pending} 封待领取
                </Link>
              )}
              {dash.invites_pending > 0 && (
                <Link
                  to="/planet/bottles"
                  className="px-3 py-1.5 rounded-lg border border-teal-300 text-teal-800"
                >
                  {dash.invites_pending} 条认识邀请待处理
                </Link>
              )}
              {dash.sent_pending_audit > 0 && (
                <span className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600">
                  {dash.sent_pending_audit} 封审核中
                </span>
              )}
            </div>
          )}

          {dash.profile?.city && (
            <p className="text-xs text-slate-500">
              当前城市：{dash.profile.city}
              {dash.profile.location_public ? " · 已在地图公开" : " · 未公开"}
            </p>
          )}
        </section>
      )}

      <section className="grid sm:grid-cols-3 gap-4">
        {[
          {
            title: "模糊位置",
            desc: "只展示城市或抖动后的模糊坐标，可随时关闭地图可见。",
            to: "/planet/settings/privacy",
          },
          {
            title: "漂流瓶闭环",
            desc: "创建 → 审核 → 送达 → 领取 → 回信（最多三轮）。",
            to: "/planet/bottles",
          },
          {
            title: "线下活动",
            desc: "复用站内交友活动，在地图上按城市浏览。",
            to: "/planet/events",
          },
        ].map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className="block p-5 rounded-xl bg-white border border-slate-200 hover:border-teal-300 transition"
          >
            <h3 className="font-medium text-teal-950">{card.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{card.desc}</p>
          </Link>
        ))}
      </section>
    </div>
  );
}
